import React, { useState, useEffect, useRef } from 'react';
import { Card, Switch, Select, Input, Space, Typography, Alert, InputNumber } from 'antd';
import { SidebarConfig } from '../types/website';
import { videoApi } from '../services/videoApi';

const { Text } = Typography;
const { Option } = Select;

interface Video {
  id: string;
  title: string;
  videoLink: string;
  imageUrl?: string;
  duration?: number;
}

interface SidebarBuilderProps {
  config: SidebarConfig;
  onConfigChange: (config: SidebarConfig) => void;
  availableVideos?: Video[];
}

const SidebarBuilder: React.FC<SidebarBuilderProps> = ({ config, onConfigChange, availableVideos = [] }) => {
  const [searchVideos, setSearchVideos] = useState<Video[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [titleValue, setTitleValue] = useState<string>(config.title || '');
  const titleDebounceRef = useRef<NodeJS.Timeout | null>(null);

  // Load selected videos for manual mode
  useEffect(() => {
    if (config.videoSelectionMode === 'manual') {
      if (config.selectedVideoIds && config.selectedVideoIds.length > 0) {
        loadSelectedVideos();
      } else {
        // Load all videos initially for search
        const loadInitialVideos = async () => {
          try {
            setSearchLoading(true);
            const allVideos = await videoApi.getAll({ published: true });
            setSearchVideos(allVideos.slice(0, 50));
          } catch (error) {
            console.error('Failed to load videos:', error);
          } finally {
            setSearchLoading(false);
          }
        };
        loadInitialVideos();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.videoSelectionMode]);

  const loadSelectedVideos = async () => {
    try {
      setSearchLoading(true);
      const allVideos = await videoApi.getAll({ published: true });
      
      if (config.selectedVideoIds && config.selectedVideoIds.length > 0) {
        // Load selected videos and add them to search results
        const selected = allVideos.filter(v => config.selectedVideoIds?.includes(v.id));
        const notSelected = allVideos.filter(v => !config.selectedVideoIds?.includes(v.id));
        // Show selected videos first, then others
        setSearchVideos([...selected, ...notSelected.slice(0, 50 - selected.length)]);
      } else {
        // Show all videos
        setSearchVideos(allVideos.slice(0, 50));
      }
    } catch (error) {
      console.error('Failed to load selected videos:', error);
      setSearchVideos([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSearch = async (value: string) => {
    setSearchQuery(value);
    
    try {
      setSearchLoading(true);
      const allVideos = await videoApi.getAll({ published: true });
      
      if (!value || value.length < 2) {
        // Show all videos if no search query or query too short
        setSearchVideos(allVideos.slice(0, 50));
      } else {
        // Filter by search query
        const filtered = allVideos.filter(video => 
          video.title.toLowerCase().includes(value.toLowerCase()) ||
          video.description?.toLowerCase().includes(value.toLowerCase())
        );
        setSearchVideos(filtered.slice(0, 50)); // Limit to 50 results
      }
    } catch (error) {
      console.error('Failed to search videos:', error);
      setSearchVideos([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleEnableChange = (enabled: boolean) => {
    onConfigChange({
      ...config,
      enabled,
    });
  };

  const handlePositionChange = (position: 'left' | 'right' | 'none') => {
    onConfigChange({
      ...config,
      position,
    });
  };

  const handleWidthChange = (width: '250px' | '300px' | '350px' | 'custom') => {
    onConfigChange({
      ...config,
      width,
    });
  };

  const handleCustomWidthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onConfigChange({
      ...config,
      customWidth: e.target.value,
    });
  };

  // Sync titleValue with config.title when config changes externally
  useEffect(() => {
    setTitleValue(config.title || '');
  }, [config.title]);

  // Cleanup debounce timeout on unmount
  useEffect(() => {
    return () => {
      if (titleDebounceRef.current) {
        clearTimeout(titleDebounceRef.current);
      }
    };
  }, []);

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setTitleValue(newValue); // Update local state immediately for UI responsiveness
    
    // Clear previous timeout
    if (titleDebounceRef.current) {
      clearTimeout(titleDebounceRef.current);
    }
    
    // Set new timeout for debounce (500ms delay)
    titleDebounceRef.current = setTimeout(() => {
      onConfigChange({
        ...config,
        title: newValue,
      });
    }, 500);
  };

  const handleVideoSelectionModeChange = (mode: 'random' | 'rank' | 'manual') => {
    onConfigChange({
      ...config,
      videoSelectionMode: mode,
      selectedVideoIds: mode === 'manual' ? (config.selectedVideoIds || []) : undefined,
      rankFrom: mode === 'rank' ? (config.rankFrom || 1) : undefined,
      rankTo: mode === 'rank' ? (config.rankTo || 6) : undefined,
    });
  };

  const handleManualVideoChange = (videoIds: string[]) => {
    // Limit to max 6 videos
    const limitedIds = videoIds.slice(0, 6);
    onConfigChange({
      ...config,
      selectedVideoIds: limitedIds,
    });
  };

  const handleRankFromChange = (value: number | null) => {
    onConfigChange({
      ...config,
      rankFrom: value || 1,
    });
  };

  const handleRankToChange = (value: number | null) => {
    onConfigChange({
      ...config,
      rankTo: value || 6,
    });
  };

  return (
    <Card title="Sidebar Builder" style={{ marginBottom: 16 }}>
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        {/* Enable/Disable Toggle */}
        <Space>
          <Switch
            checked={config.enabled}
            onChange={handleEnableChange}
            checkedChildren="Enabled"
            unCheckedChildren="Disabled"
          />
          <Text>Enable Sidebar</Text>
        </Space>

        {/* Position and Width Selection (only show if enabled) */}
        {config.enabled && (
          <>
            {/* Position Selection */}
            <div>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>
                Position:
              </Text>
              <Select
                value={config.position}
                onChange={handlePositionChange}
                style={{ width: '100%' }}
              >
                <Option value="left">Left Sidebar</Option>
                <Option value="right">Right Sidebar</Option>
                <Option value="none">No Sidebar</Option>
              </Select>
            </div>

            {/* Width Selection */}
            {config.position !== 'none' && (
              <>
                <div>
                  <Text strong style={{ display: 'block', marginBottom: 8 }}>
                    Width:
                  </Text>
                  <Select
                    value={config.width}
                    onChange={handleWidthChange}
                    style={{ width: '100%', marginBottom: config.width === 'custom' ? 8 : 16 }}
                  >
                    <Option value="250px">250px</Option>
                    <Option value="300px">300px (Default)</Option>
                    <Option value="350px">350px</Option>
                    <Option value="custom">Custom Width</Option>
                  </Select>

                  {/* Custom Width Input */}
                  {config.width === 'custom' && (
                    <Input
                      placeholder="Enter custom width (e.g., 280px, 20%)"
                      value={config.customWidth}
                      onChange={handleCustomWidthChange}
                      style={{ width: '100%', marginTop: 8, marginBottom: 16 }}
                    />
                  )}
                </div>

                {/* Title Input */}
                <div>
                  <Text strong style={{ display: 'block', marginBottom: 8 }}>
                    Sidebar Title:
                  </Text>
                  <Input
                    placeholder="Enter sidebar title (optional)"
                    value={titleValue}
                    onChange={handleTitleChange}
                    style={{ width: '100%' }}
                    allowClear
                  />
                  <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
                    Title will be displayed at the top of the sidebar
                  </Text>
                </div>

                {/* Video Selection */}
                <div>
                  <Text strong style={{ display: 'block', marginBottom: 8 }}>
                    Video Selection Mode:
                  </Text>
                  <Select
                    value={config.videoSelectionMode || 'random'}
                    onChange={handleVideoSelectionModeChange}
                    style={{ width: '100%', marginBottom: 16 }}
                  >
                    <Option value="random">Random 6 Videos</Option>
                    <Option value="rank">Rank From - To</Option>
                    <Option value="manual">Manual Selection</Option>
                  </Select>

                  {/* Random Mode */}
                  {config.videoSelectionMode === 'random' && (
                    <Alert
                      message="6 random videos will be automatically selected when generating the website"
                      type="info"
                      showIcon
                    />
                  )}

                  {/* Rank Mode */}
                  {config.videoSelectionMode === 'rank' && (
                    <div>
                      <Space style={{ width: '100%' }} size="middle">
                        <div style={{ flex: 1 }}>
                          <Text strong style={{ display: 'block', marginBottom: 8 }}>
                            Rank From:
                          </Text>
                          <InputNumber
                            min={1}
                            value={config.rankFrom || 1}
                            onChange={handleRankFromChange}
                            style={{ width: '100%' }}
                          />
                        </div>
                        <div style={{ flex: 1 }}>
                          <Text strong style={{ display: 'block', marginBottom: 8 }}>
                            Rank To:
                          </Text>
                          <InputNumber
                            min={1}
                            value={config.rankTo || 6}
                            onChange={handleRankToChange}
                            style={{ width: '100%' }}
                          />
                        </div>
                      </Space>
                      <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 8 }}>
                        Videos will be selected from rank {config.rankFrom || 1} to {config.rankTo || 6} (max 6 videos)
                      </Text>
                    </div>
                  )}

                  {/* Manual Mode */}
                  {config.videoSelectionMode === 'manual' && (
                    <div>
                      <Text strong style={{ display: 'block', marginBottom: 8 }}>
                        Select Videos (Max 6):
                      </Text>
                      {config.selectedVideoIds && config.selectedVideoIds.length >= 6 && (
                        <Alert
                          message="Maximum 6 videos selected"
                          type="info"
                          showIcon
                          style={{ marginBottom: 12 }}
                        />
                      )}
                      <Select
                        mode="multiple"
                        showSearch
                        placeholder="Search and select videos (type to search)"
                        value={config.selectedVideoIds || []}
                        onChange={handleManualVideoChange}
                        onSearch={handleSearch}
                        loading={searchLoading}
                        filterOption={false}
                        maxTagCount={6}
                        style={{ width: '100%' }}
                        notFoundContent={searchLoading ? 'Searching...' : searchQuery.length < 2 ? 'Type at least 2 characters to search' : 'No videos found'}
                      >
                        {searchVideos.map((video) => (
                          <Option key={video.id} value={video.id}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              {video.imageUrl && (
                                <img
                                  src={video.imageUrl}
                                  alt={video.title}
                                  style={{ width: '40px', height: '30px', objectFit: 'cover', borderRadius: '4px' }}
                                />
                              )}
                              <div style={{ flex: 1 }}>
                                <Text strong style={{ fontSize: '13px', display: 'block' }}>
                                  {video.title}
                                </Text>
                                {video.duration && (
                                  <Text type="secondary" style={{ fontSize: '11px' }}>
                                    {Math.floor(video.duration / 60)}:{(video.duration % 60).toString().padStart(2, '0')}
                                  </Text>
                                )}
                              </div>
                            </div>
                          </Option>
                        ))}
                      </Select>
                      <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 8 }}>
                        Selected: {config.selectedVideoIds?.length || 0} / 6 videos
                      </Text>
                    </div>
                  )}
                </div>
              </>
            )}
          </>
        )}

        {/* Info Message */}
        {config.enabled && config.position !== 'none' && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            Sidebar will be displayed on the {config.position} side of the content area.
          </Text>
        )}
      </Space>
    </Card>
  );
};

export default SidebarBuilder;

